import { withResourceRecords } from '@/shared/contexts'
import { useResourceRecordsContext } from '@/shared/hooks'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { LoadWrapper } from '@/shared/ui'
import { FilterChips, PageHeader, ResourceFilterBar, Screen } from '@/shared/ui/Layout'

import { ScheduleActiveFilters } from './components/ScheduleActiveFilters'
import { ScheduleAdvancedFilter } from './components/ScheduleAdvancedFilter'
import { ScheduleDayTabs } from './components/ScheduleDayTabs'
import { ScheduleDetailSheet } from './components/ScheduleDetailSheet'
import { ScheduleEmpty } from './components/ScheduleEmpty'
import { ScheduleListSkeleton } from './components/ScheduleListSkeleton'
import { ScheduleSessionCard } from './components/ScheduleSessionCard'
import { ScheduleStickySkeleton } from './components/ScheduleStickySkeleton'
import { getConfig } from './config'
import { useScheduleView } from './hooks/useScheduleView'
import type { ScheduleTimeFilter, Session } from './model/types'

const SchedulePage = () => {
  const { t } = useTranslation()
  const {
    records = [],
    isLoading: isScheduleLoading,
    isSuccess: isScheduleSuccess
  } = useResourceRecordsContext() as {
    records?: Session[]
    isLoading?: boolean
    isSuccess?: boolean
  }
  const view = useScheduleView()

  /**
   * O check-in concluído muda duas coisas na tela: a sessão passa a contar como
   * concluída na listagem, e a folha de detalhe não tem mais o que pedir.
   */
  const handleCheckInFinished = () => {
    view.refetch?.()
    view.setDetailSession(null)
  }

  const showInitialSkeleton =
    records.length === 0 && (isScheduleLoading || !isScheduleSuccess)

  const activeSpeakerName = view.advancedFilters.filters.speaker
    ? view.filterOptions.speakers.find(s => s.id === view.advancedFilters.filters.speaker)?.label
    : null

  const renderSessionCard = (session: Session) => (
    <ScheduleSessionCard
      key={session.id}
      session={session}
      onStartCheckIn={view.setDetailSession}
      onClick={view.setDetailSession}
    />
  )

  return (
    <Screen>
      <Screen.Context>
        <PageHeader title={view.humanName.plural} />
      </Screen.Context>

      <Screen.Sticky className="flex flex-col gap-2">
        {showInitialSkeleton ? (
          <ScheduleStickySkeleton />
        ) : (
          <>
            <ScheduleDayTabs
              days={view.days}
              selectedDay={view.selectedDay}
              onSelect={view.setSelectedDay}
            />

            <ResourceFilterBar
              searchValue={view.searchQuery}
              onSearchChange={view.setSearchQuery}
              searchPlaceholder={t('schedule.search.placeholder')}
              advancedFilter={
                <ScheduleAdvancedFilter
                  options={view.filterOptions}
                  filters={view.advancedFilters.filters}
                  hasActiveFilters={view.advancedFilters.hasActiveFilters}
                  onPlaceChange={view.advancedFilters.setPlace}
                  onSpeakerChange={view.advancedFilters.setSpeaker}
                  onKindChange={view.advancedFilters.setKind}
                  onReset={view.advancedFilters.reset}
                  className="shrink-0"
                />
              }
            />

            <FilterChips<ScheduleTimeFilter>
              options={view.visibleTimeFilters}
              value={view.timeFilter}
              onChange={view.setTimeFilter}
              aria-label={t('schedule.filters.ariaLabel')}
            />

            <ScheduleActiveFilters
              filters={view.advancedFilters.filters}
              speakerName={activeSpeakerName}
              onRemovePlace={() => view.advancedFilters.setPlace(null)}
              onRemoveSpeaker={() => view.advancedFilters.setSpeaker(null)}
              onRemoveKind={() => view.advancedFilters.setKind(null)}
            />
          </>
        )}
      </Screen.Sticky>

      <Screen.Content className="pt-4">
        {showInitialSkeleton ? (
          <ScheduleListSkeleton />
        ) : (
        <LoadWrapper
          {...view.loadWrapperConfig}
          skeleton={<ScheduleListSkeleton />}
        >
          {!view.isEmpty && (
            <div className="flex flex-col gap-3 pb-4">
              {view.filteredSessions.map(renderSessionCard)}
            </div>
          )}

          {view.isEmpty && (
            <ScheduleEmpty
              hasSearch={view.hasSearch}
              hasActiveFilters={view.hasActiveFilters}
              onClearFilters={view.clearFilters}
            />
          )}
        </LoadWrapper>
        )}

        <ScheduleDetailSheet
          session={view.detailSession}
          onClose={() => view.setDetailSession(null)}
          onCheckInFinished={handleCheckInFinished}
        />
      </Screen.Content>
    </Screen>
  )
}

export const DigitalProductSchedule = withResourceRecords(
  SchedulePage,
  getConfig
)
